import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ExclamationTriangleIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  LockClosedIcon,
  UserGroupIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import { legalService, LegalHold, HOLD_STATUSES } from '../services/legal.service';

const CONTAINER_VARIANTS = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
};

const ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: 'easeOut' } },
};

const STATUS_BADGE_COLORS: Record<string, string> = {
  success: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
  warning: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
  danger: 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300',
  secondary: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300',
};

const HOLD_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  RELEASED: 'Released',
};

function HoldCard({ hold }: { hold: LegalHold }) {
  const statusCfg = legalService.getHoldStatusConfig(hold.status);

  return (
    <Link to={`/legal/holds/${hold.id}`} className="block">
      <Card hover="default" padding="none" className="transition-all duration-200 cursor-pointer">
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-lg text-secondary-900 dark:text-white truncate">
                {hold.holdName}
              </h3>
              {hold.matterTitle && (
                <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-0.5 truncate">
                  Matter: {hold.matterTitle}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <span
              className={cn(
                'px-3 py-1 text-xs font-medium rounded-full',
                STATUS_BADGE_COLORS[statusCfg.color]
              )}
            >
              {statusCfg.label}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div className="flex items-center gap-2">
              <UserGroupIcon className="w-4 h-4 text-secondary-500 dark:text-secondary-400 flex-shrink-0" />
              <span className="text-secondary-600 dark:text-secondary-400">
                {hold._count?.custodians ?? 0} custodian
                {(hold._count?.custodians ?? 0) !== 1 ? 's' : ''}
              </span>
            </div>
            {hold.issuedDate && (
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-secondary-500 dark:text-secondary-400 flex-shrink-0" />
                <span className="text-secondary-600 dark:text-secondary-400">
                  Issued {legalService.formatDate(hold.issuedDate)}
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function LegalHoldsList() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [holds, setHolds] = useState<LegalHold[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalHolds, setTotalHolds] = useState(0);

  const fetchHolds = useCallback(async () => {
    try {
      setLoading(true);
      const data = await legalService.getHolds({
        page: currentPage,
        limit: 20,
        status: selectedStatus !== 'all' ? selectedStatus : undefined,
      });
      setHolds(data.holds);
      setTotalPages(data.pagination.totalPages);
      setTotalHolds(data.pagination.total);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch holds:', err instanceof Error ? err.message : err);
      setError('Failed to load legal holds');
      setHolds([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedStatus]);

  useEffect(() => {
    fetchHolds();
  }, [fetchHolds]);

  // Filter by search client-side (server doesn't have search for holds)
  const filtered = searchQuery
    ? holds.filter(
        (h) =>
          h.holdName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          h.matterTitle?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : holds;

  // Stats
  const activeCount = holds.filter((h) => h.status === 'ACTIVE').length;
  const draftCount = holds.filter((h) => h.status === 'DRAFT').length;
  const releasedCount = holds.filter((h) => h.status === 'RELEASED').length;

  const selectCls =
    'px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm';

  return (
    <motion.div
      variants={CONTAINER_VARIANTS}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-6"
    >
      {/* Header */}
      <motion.div variants={ITEM_VARIANTS}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
              Legal Holds
            </h1>
            <p className="text-secondary-600 dark:text-secondary-400 mt-1">
              Manage litigation holds and data preservation orders
            </p>
          </div>
          <Link to="/legal/holds/create">
            <Button className="w-full sm:w-auto">
              <PlusIcon className="w-4 h-4 mr-2" />
              New Hold
            </Button>
          </Link>
        </div>
      </motion.div>

      {/* Stats */}
      <motion.div variants={ITEM_VARIANTS}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-secondary-900 dark:text-white">
                {totalHolds}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">Total</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-success-600 dark:text-success-400">
                {activeCount}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">Active</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-secondary-600 dark:text-secondary-400">
                {draftCount}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">Draft</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-warning-600 dark:text-warning-400">
                {releasedCount}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">Released</div>
            </CardContent>
          </Card>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div variants={ITEM_VARIANTS}>
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Search holds..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
                />
              </div>
              <select
                value={selectedStatus}
                onChange={(e) => { setSelectedStatus(e.target.value); setCurrentPage(1); }}
                className={selectCls}
              >
                <option value="all">All Statuses</option>
                {HOLD_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {HOLD_STATUS_LABELS[s] ?? s}
                  </option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* List */}
      <motion.div variants={ITEM_VARIANTS}>
        {loading ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="text-lg text-secondary-600 dark:text-secondary-400">
                Loading holds...
              </div>
            </CardContent>
          </Card>
        ) : error ? (
          <Card>
            <CardContent className="p-12 text-center">
              <ExclamationTriangleIcon className="w-12 h-12 text-danger-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                Error Loading Holds
              </h3>
              <p className="text-secondary-600 dark:text-secondary-400 mb-4">{error}</p>
              <Button onClick={fetchHolds} variant="outline">
                Try Again
              </Button>
            </CardContent>
          </Card>
        ) : filtered.length > 0 ? (
          <div className="space-y-4">
            {filtered.map((hold) => (
              <HoldCard key={hold.id} hold={hold} />
            ))}

            {/* Pagination */}
            <div className="flex items-center justify-between pt-2">
              <span className="text-sm text-secondary-600 dark:text-secondary-400">
                Showing {filtered.length} of {totalHolds} holds
              </span>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-secondary-600 dark:text-secondary-400">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <LockClosedIcon className="w-12 h-12 text-secondary-400 dark:text-secondary-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                No holds found
              </h3>
              <p className="text-secondary-600 dark:text-secondary-400">
                No legal holds match your current filters.
              </p>
            </CardContent>
          </Card>
        )}
      </motion.div>
    </motion.div>
  );
}
